import React, { useState, useEffect, useCallback } from 'react';
import { Tabs, Tab, Typography, TextField, Button, Paper, IconButton } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import AddIcon from '@material-ui/icons/Add';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import { PlannerSideNav, SectionCard } from '../../shared_components';
import { DeletePopUp } from '../../../../shared_elements';
import StageConfigKpis from '../components/StageConfigKpis';
import StagesTab from '../components/StagesTab';
import TransitionsTab from '../components/TransitionsTab';
import DiagramTab from '../components/DiagramTab';
import StageTemplateDialog from '../components/StageTemplateDialog';
import StageFormDialog from '../components/StageFormDialog';
import { TEXT, SPACE, pageSx, useSnackbar } from '../../shared_components';
import '../../shared_components/assets/styles/plannerTracker.scss';
import { checkApiStatus } from '../../../../utils_v2';
import { trackActivity } from '../../../../utils/mixpanel';
import { globalGetService, globalDeleteService, globalPutService } from '../../../../utils_v2/globalApiServices';
import { getStageTemplatesApi, getTemplateStagesApi, getTransitionsApi, deleteStageTemplateApi, deleteStageApi, reorderTemplateStagesApi } from '../apiService';

const TABS = [
  { key: 'stages', label: 'Stages & Order' },
  { key: 'transitions', label: 'Transition Rules' },
  { key: 'diagram', label: 'Workflow Diagram' },
];

const stagesErrorMessage = (response) => response?.data?.message || 'Unable to load stages for this template.';
const templatesErrorMessage = (response) => response?.data?.message || 'Unable to load stage config templates.';
const transitionsErrorMessage = (response) => response?.data?.message || 'Unable to load transitions for this template.';
const templateDeleteErrorMessage = (response) => response?.data?.message || 'Unable to delete the template due to a technical error.';
const stageDeleteErrorMessage = (response) => response?.data?.message || 'Unable to delete the stage due to a technical error.';

const StageConfig = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [tab, setTab] = useState('stages');
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [addEditTemplate, setAddEditTemplate] = useState({ modal: false, mode: '', data: null })
  const [stages, setStages] = useState([]);
  const [loadingStages, setLoadingStages] = useState(false);
  const [addEditStage, setAddEditStage] = useState({ modal: false, mode: '', data: null });
  const [transitions, setTransitions] = useState([]);
  const [loadingTransitions, setLoadingTransitions] = useState(false);
  const [showAddTransitionDialog, setShowAddTransitionDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // { type: 'template' | 'stage', item }
  const [deleting, setDeleting] = useState(false);
  const selectedTemplate = (templates || []).find((t) => t?.id === selectedTemplateId) || null;

  const fetchTemplates = useCallback(() => {
    setLoadingTemplates(true);
    globalGetService(getStageTemplatesApi()).then((response) => {
      setLoadingTemplates(false);
      if (checkApiStatus(response)) {
        const list = response?.data?.data || [];
        setTemplates(list);
        setSelectedTemplateId((prev) => (prev && list.some((t) => t.id === prev) ? prev : (list[0] || {}).id || null));
      } else {
        enqueueSnackbar(templatesErrorMessage(response), { variant: 'error' });
      }
    });
  }, []);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  useEffect(() => {
    trackActivity('Project Management', {
      event_type: 'Page Visited',
      page_title: 'Planner Tracker Stage Config',
    });
  }, []);

  const fetchStages = useCallback(() => {
    if (!selectedTemplateId) { setStages([]); return; }
    setLoadingStages(true);
    globalGetService(getTemplateStagesApi(selectedTemplateId)).then((response) => {
      setLoadingStages(false);
      if (checkApiStatus(response)) {
        const list = response?.data?.data || [];
        setStages(list);
      } else {
        enqueueSnackbar(stagesErrorMessage(response), { variant: 'error' });
      }
    });
  }, [selectedTemplateId]);

  useEffect(() => { setStages([]); fetchStages(); }, [fetchStages]);

  const fetchTransitions = useCallback(() => {
    if (!selectedTemplateId) { setTransitions([]); return; }
    setLoadingTransitions(true);
    globalGetService(getTransitionsApi(), { template_id: selectedTemplateId }).then((response) => {
      setLoadingTransitions(false);
      if (checkApiStatus(response)) {
        setTransitions(response?.data?.data || []);
      } else {
        enqueueSnackbar(transitionsErrorMessage(response), { variant: 'error' });
      }
    });
  }, [selectedTemplateId]);

  useEffect(() => { setTransitions([]); fetchTransitions(); }, [fetchTransitions]);

  const handleStageSaved = () => {
    setAddEditStage({ modal: false, mode: '', data: null });
    fetchStages();
  };

  const handleReorderStages = (reorderedStages) => {
    const previousStages = stages;
    const renumberedStages = reorderedStages.map((stage, index) => ({ ...stage, order: index + 1 }));
    setStages(renumberedStages);

    const ordering = renumberedStages.map((stage) => ({ id: stage?.id, order: stage?.order }));

    globalPutService(reorderTemplateStagesApi(selectedTemplateId), { ordering }).then((response) => {
      if (checkApiStatus(response)) {
        enqueueSnackbar('Stage order updated', { variant: 'success' });
        trackActivity('Project Management', {
          event_type: 'Reorder Stages Success',
          page_title: 'Planner Tracker Stage Config',
          template_id: selectedTemplateId || '',
          request_body: { ordering },
          response_msg: response?.data?.message,
        });
      } else {
        setStages(previousStages);
        enqueueSnackbar(response?.data?.message || 'Unable to reorder stages due to a technical error.', { variant: 'error' });
        trackActivity('Project Management', {
          event_type: 'Reorder Stages Failed',
          page_title: 'Planner Tracker Stage Config',
          template_id: selectedTemplateId || '',
          request_body: { ordering },
          error_source: 'Backend',
          error_message: response?.data?.message || 'Unable to reorder stages due to a technical error.',
        });
      }
    });
  };

  const handleConfirmDelete = () => {
    if (!deleteTarget) return;
    setDeleting(true);
    const { type, item } = deleteTarget;
    const request = type === 'template' ? globalDeleteService(deleteStageTemplateApi(item?.id)) : globalDeleteService(deleteStageApi(item?.id));
    const errorMessage = type === 'template' ? templateDeleteErrorMessage : stageDeleteErrorMessage;
    const entityLabel = type === 'template' ? 'Stage Template' : 'Stage';

    request.then((response) => {
      setDeleting(false);
      if (checkApiStatus(response)) {
        enqueueSnackbar(type === 'template' ? 'Template deleted' : 'Stage deleted', { variant: 'success' });
        trackActivity('Project Management', {
          event_type: `Delete ${entityLabel} Success`,
          page_title: 'Planner Tracker Stage Config',
          template_id: selectedTemplateId || '',
          [type === 'template' ? 'template_id' : 'stage_id']: item?.id || '',
          response_msg: response?.data?.message,
        });
        setDeleteTarget(null);
        if (type === 'template') {
          setSelectedTemplateId(null);
          fetchTemplates();
        } else {
          fetchStages();
        }
      } else {
        enqueueSnackbar(errorMessage(response), { variant: 'error' });
        trackActivity('Project Management', {
          event_type: `Delete ${entityLabel} Failed`,
          page_title: 'Planner Tracker Stage Config',
          template_id: selectedTemplateId || '',
          [type === 'template' ? 'template_id' : 'stage_id']: item?.id || '',
          error_source: 'Backend',
          error_message: errorMessage(response),
        });
      }
    });
  };

  const deleteContent = () => {
    if (!deleteTarget) return null;
    const { type, item } = deleteTarget;
    if (type === 'template') return <h4>Are you sure you want to delete the template &quot;{item?.name}&quot;? This will remove all its stages and transitions.</h4>;
    return <h4>Are you sure you want to delete the stage &quot;{item?.name}&quot;?</h4>;
  };

  return (
    <div style={{ display: 'flex', marginLeft: 56, marginTop: 15 }}>
      <PlannerSideNav activeKey="stages" />
      <section className="projects-management-apps" style={{ ...pageSx, flex: 1, minWidth: 0 }}>
        <SectionCard style={{ marginBottom: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: SPACE.md, borderLeft: '4px solid #a371f7' }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <Autocomplete
              options={templates || []}
              disableClearable
              style={{ minWidth: 260 }}
              getOptionLabel={(option) => `${option?.name || ''}${option?.is_default ? ' (Default)' : ''}${option?.is_active === false ? ' — Inactive' : ''}`}
              getOptionSelected={(option, current) => option?.id === current?.id}
              value={(templates || []).find((t) => t?.id === selectedTemplateId) || null}
              onChange={(event, newValue) => setSelectedTemplateId(newValue?.id || null)}
              loading={loadingTemplates}
              noOptionsText={loadingTemplates ? 'Loading templates…' : 'No templates yet'}
              renderInput={(params) => (
                <TextField {...params} label="Template" placeholder="Select template" variant="outlined" size="small" />
              )}
            />
            {selectedTemplate ? (
              <IconButton size="small" onClick={() => setAddEditTemplate({ modal: true, mode: 'edit', data: selectedTemplate })} style={{ border: '1px solid #DCE1EA', borderRadius: 9 }}>
                <EditOutlinedIcon fontSize="small" style={{ color: '#344054' }} />
              </IconButton>
            ) : null}
            {selectedTemplate ? (
              <IconButton size="small" onClick={() => setDeleteTarget({ type: 'template', item: selectedTemplate })} style={{ border: '1px solid #FFD2D2', borderRadius: 9, color: '#F04444' }}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            ) : null}
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            {selectedTemplateId && tab === 'stages' ? (
              <Button variant="outlined" color="primary" size="small" startIcon={<AddIcon />} onClick={() => setAddEditStage({ modal: true, mode: 'create', data: null })}>
                Add Stage
              </Button>
            ) : null}
            {selectedTemplateId && tab === 'transitions' ? (
              <Button variant="outlined" color="primary" size="small" startIcon={<AddIcon />} onClick={() => setShowAddTransitionDialog(true)} disabled={!(stages || []).length}>
                Add Transition
              </Button>
            ) : null}
            <Button variant="contained" color="primary" size="small" startIcon={<AddIcon />} onClick={() => setAddEditTemplate({ modal: true, mode: 'create', data: null })}>
              New Template
            </Button>
          </div>
        </SectionCard>

        <StageConfigKpis templates={templates} selectedTemplateId={selectedTemplateId} />

        <SectionCard noPadding style={{ marginBottom: 10 }}>
          <Tabs
            variant="scrollable"
            scrollButtons="auto"
            value={tab}
            onChange={(event, value) => setTab(value)}
            indicatorColor="primary"
            textColor="primary"
          >
            {TABS.map((t) => <Tab key={t.key} value={t.key} label={t.label} />)}
          </Tabs>
        </SectionCard>

        <div className="pt-scroll-panel pt-scroll-panel--stage-config">
          <Paper style={{ padding: '10px 10px' }}>
            {tab === 'stages' ? (
              <StagesTab
                templateId={selectedTemplateId}
                stages={stages}
                loading={loadingStages}
                onEditStage={(target) => setAddEditStage({ modal: true, mode: 'edit', data: target })}
                onDeleteStage={(target) => setDeleteTarget({ type: 'stage', item: target })}
                onReorderStages={handleReorderStages}
              />
            ) : null}

            {tab === 'transitions' ? (
              <TransitionsTab
                templateId={selectedTemplateId}
                stages={stages}
                transitions={transitions}
                loading={loadingTransitions}
                onTransitionsChanged={fetchTransitions}
                enqueueSnackbar={enqueueSnackbar}
                showAddTransition={showAddTransitionDialog}
                onCloseAddTransition={() => setShowAddTransitionDialog(false)}
              />
            ) : null}
            {tab === 'diagram' ? <DiagramTab stages={stages} transitions={transitions} /> : null}
          </Paper>
        </div>
      </section>

      {addEditTemplate?.modal ? (
        <StageTemplateDialog
          addEditTemplate={addEditTemplate}
          onToggleModal={() => setAddEditTemplate({ modal: false, mode: '', data: null })}
          getResponseBack={() => { setAddEditTemplate({ modal: false, mode: '', data: null }); fetchTemplates(); }}
          enqueueSnackbar={enqueueSnackbar}
        />
      ) : null}

      {addEditStage?.modal ? (
        <StageFormDialog
          templateId={selectedTemplateId}
          addEditStage={addEditStage}
          onToggleModal={() => setAddEditStage({ modal: false, mode: '', data: null })}
          getResponseBack={handleStageSaved}
          enqueueSnackbar={enqueueSnackbar}
        />
      ) : null}

      {deleteTarget ? (
        <DeletePopUp
          modal
          title={`Delete ${deleteTarget.type === 'template' ? 'Template' : 'Stage'}`}
          content={deleteContent()}
          toggleModalFn={() => setDeleteTarget(null)}
          deleteRecordFn={handleConfirmDelete}
          circularloader={deleting}
        />
      ) : null}
    </div>
  );
};

export default StageConfig;
